import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {doc, getDoc, onSnapshot, setDoc, serverTimestamp} from "firebase/firestore";
import {UserContext} from "./UserContext";
import {db} from "../firebase";
import {useAlert} from "./AlertContext";
import {applyBeatPackDiscount} from "../lib/beatPricing";
import {
  GUEST_CART_STORAGE_KEY,
  cartDisplayName,
  cartItemIdentity,
  cartProductType,
  cartSelectionGroup,
  parseGuestCart,
  sanitizeCartItem,
  sanitizeCartItems,
  serializeGuestCart,
} from "../lib/cartItemIdentity";
import {beatAnalyticsParams, trackCommerceEvent} from "../lib/commerceAnalytics";

export const CartContext = createContext(null);

function initialGuestItems() {
  if (typeof window === "undefined") return [];
  try {
    const stored = window.localStorage.getItem(GUEST_CART_STORAGE_KEY);
    const restored = parseGuestCart(stored);
    if (stored && !restored.length) window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
    return restored;
  } catch {
    return [];
  }
}

function cartFingerprint(list) {
  return JSON.stringify(sanitizeCartItems(list).map((item) => ({
    identity: cartItemIdentity(item),
    price: Number(item.price) || 0,
    basePrice: Number(item.basePrice ?? item.price) || 0,
    beatPack: Boolean(item.isBeatPack),
    discounted: Boolean(item.discounted),
  })));
}

function mergeCartItems(primary, secondary) {
  const merged = sanitizeCartItems(primary);
  const identities = new Set(merged.map(cartItemIdentity));
  const groups = new Set(merged.map(cartSelectionGroup).filter(Boolean));
  for (const item of sanitizeCartItems(secondary)) {
    const identity = cartItemIdentity(item);
    const group = cartSelectionGroup(item);
    if (!identity || identities.has(identity) || (group && groups.has(group))) continue;
    merged.push(item);
    identities.add(identity);
    if (group) groups.add(group);
  }
  return merged;
}

export function CartProvider({children}) {
  const {alert} = useAlert();
  const user = (useContext(UserContext) || {}).user || null;
  const [items, setItems] = useState(initialGuestItems);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [syncedUserId, setSyncedUserId] = useState(null);
  const itemsRef = useRef(items);
  const cartWriteInFlightRef = useRef(false);
  const lastSavedSigRef = useRef("");
  const prevDiscountApplied = useRef(false);
  const lastBatchAddAtRef = useRef(0);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const applyBeatPackPricing = useCallback((list) => {
    const safe = sanitizeCartItems(list);
    const eligibleCount = safe.filter((item) => cartProductType(item) === "beat").length;
    const next = safe.map((item) => {
      if (cartProductType(item) !== "beat") return item;
      const calculation = applyBeatPackDiscount(
        item.basePrice ?? item.price ?? 0,
        discountPercent,
        eligibleCount,
      );
      return {
        ...item,
        basePrice: calculation.basePrice,
        price: calculation.price,
        discounted: calculation.discounted,
        isBeatPack: calculation.qualifies || Boolean(item.isBeatPack),
      };
    });

    const hasDiscount = next.some((item) => item.discounted);
    if (prevDiscountApplied.current && !hasDiscount) {
      try {
        window.alert("Discount has been removed, you need ten or more tracks for the discount.");
      } catch {}
    }
    prevDiscountApplied.current = hasDiscount;
    return next;
  }, [discountPercent]);

  useEffect(() => {
    setItems((previous) => applyBeatPackPricing(previous));
  }, [applyBeatPackPricing]);

  // Guests keep a small, validated, versioned selection snapshot. Prices are
  // display-only; checkout re-prices identities on the server.
  useEffect(() => {
    if (user?.uid || typeof window === "undefined") return;
    try {
      if (items.length) window.localStorage.setItem(GUEST_CART_STORAGE_KEY, serializeGuestCart(items));
      else window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
      window.localStorage.removeItem("cart_items");
    } catch {}
  }, [items, user?.uid]);

  const clearCart = useCallback(async () => {
    setItems([]);
    try {
      window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
      window.localStorage.removeItem("cart");
      window.localStorage.removeItem("cart_items");
      window.sessionStorage.removeItem("cart");
    } catch {}

    lastSavedSigRef.current = cartFingerprint([]);
    if (!user?.uid) return;
    try {
      cartWriteInFlightRef.current = true;
      await setDoc(
        doc(db, "users", user.uid),
        {cartItems: [], cartUpdatedAt: serverTimestamp()},
        {merge: true},
      );
    } finally {
      cartWriteInFlightRef.current = false;
    }
  }, [user?.uid]);

  // A signed-in user's Firestore cart is authoritative across devices. Any
  // guest selections present at login are merged by canonical identity once.
  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;
    setSyncedUserId(null);
    if (!user?.uid) return () => unsubscribe();

    const uid = user.uid;
    const userRef = doc(db, "users", uid);
    (async () => {
      let serverItems = [];
      try {
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) serverItems = snapshot.data()?.cartItems || [];
      } catch {}
      if (cancelled) return;

      const priced = applyBeatPackPricing(mergeCartItems(serverItems, itemsRef.current));
      const serverSafe = sanitizeCartItems(serverItems);
      setItems(priced);
      setSyncedUserId(uid);
      try {
        window.localStorage.removeItem(GUEST_CART_STORAGE_KEY);
      } catch {}

      const mergedSig = cartFingerprint(priced);
      lastSavedSigRef.current = mergedSig;
      if (mergedSig !== cartFingerprint(serverSafe)) {
        try {
          cartWriteInFlightRef.current = true;
          await setDoc(
            userRef,
            {cartItems: sanitizeCartItems(priced), cartUpdatedAt: serverTimestamp()},
            {merge: true},
          );
        } finally {
          cartWriteInFlightRef.current = false;
        }
      }
      if (cancelled) return;

      unsubscribe = onSnapshot(userRef, (snapshot) => {
        if (cartWriteInFlightRef.current) return;
        const fromServer = snapshot.exists() && Array.isArray(snapshot.data()?.cartItems)
          ? snapshot.data().cartItems
          : [];
        const pricedServerItems = applyBeatPackPricing(fromServer);
        if (cartFingerprint(itemsRef.current) !== cartFingerprint(pricedServerItems)) {
          setItems(pricedServerItems);
        }
        lastSavedSigRef.current = cartFingerprint(pricedServerItems);
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user?.uid, applyBeatPackPricing]);

  useEffect(() => {
    if (!user?.uid || syncedUserId !== user.uid) return;
    const payload = sanitizeCartItems(items);
    const signature = cartFingerprint(payload);
    if (signature === lastSavedSigRef.current) return;
    const timer = setTimeout(async () => {
      try {
        cartWriteInFlightRef.current = true;
        await setDoc(
          doc(db, "users", user.uid),
          {cartItems: payload, cartUpdatedAt: serverTimestamp()},
          {merge: true},
        );
        lastSavedSigRef.current = signature;
      } finally {
        cartWriteInFlightRef.current = false;
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [items, syncedUserId, user?.uid]);

  const addItem = useCallback((rawItem) => {
    const item = sanitizeCartItem(rawItem);
    if (!item) return;
    setItems((previous) => {
      const identity = cartItemIdentity(item);
      const group = cartSelectionGroup(item);
      const existing = previous.find((candidate) =>
        cartItemIdentity(candidate) === identity ||
        (group && cartSelectionGroup(candidate) === group),
      );
      if (existing) {
        try {
          alert(`Hold up, ${cartDisplayName(item)} is already in your cart.`);
        } catch {}
        return previous;
      }

      if (cartProductType(item) === "beat") {
        trackCommerceEvent("add_to_cart", beatAnalyticsParams(item, {
          currency: "USD",
          value: Number(item.price) || 0,
          license_type: item.license,
        }));
      }
      if (!item.isBeatPack) {
        const recentlyBatchAdded = Date.now() - lastBatchAddAtRef.current < 1000;
        if (!recentlyBatchAdded) {
          try {
            alert(`${item.exclusive ? "Exclusive " : ""}${cartDisplayName(item)} has been added to your cart.`);
          } catch {}
        }
      }
      return applyBeatPackPricing([...previous, item]);
    });
  }, [alert, applyBeatPackPricing]);

  const addItems = useCallback((rawList) => {
    if (!Array.isArray(rawList) || !rawList.length) return;
    setItems((previous) => {
      const merged = mergeCartItems(previous, rawList);
      const addedCount = merged.length - previous.length;
      const previousIdentities = new Set(previous.map(cartItemIdentity));
      merged
        .filter((item) => !previousIdentities.has(cartItemIdentity(item)))
        .filter((item) => cartProductType(item) === "beat")
        .forEach((item) => trackCommerceEvent("add_to_cart", beatAnalyticsParams(item, {
          currency: "USD",
          value: Number(item.price) || 0,
          license_type: item.license,
        })));
      if (addedCount > 0 && rawList.some((item) => item?.isBeatPack)) {
        lastBatchAddAtRef.current = Date.now();
        try {
          alert("The BeatPack has been added to your cart.");
        } catch {}
      }
      return addedCount > 0 ? applyBeatPackPricing(merged) : previous;
    });
  }, [alert, applyBeatPackPricing]);

  const removeItem = useCallback((itemOrId) => {
    setItems((previous) => {
      const objectTarget = itemOrId && typeof itemOrId === "object" ? itemOrId : null;
      const targetIdentity = objectTarget ? cartItemIdentity(objectTarget) : "";
      const legacyId = objectTarget ? "" : String(itemOrId || "");
      const removed = previous.find((item) => targetIdentity
        ? cartItemIdentity(item) === targetIdentity
        : String(item.id) === legacyId);
      if (!removed) return previous;
      const next = previous.filter((item) => targetIdentity
        ? cartItemIdentity(item) !== targetIdentity
        : String(item.id) !== legacyId);
      const productType = cartProductType(removed);
      trackCommerceEvent("remove_from_cart", productType === "beat"
        ? beatAnalyticsParams(removed, {
          currency: "USD",
          value: Number(removed.price) || 0,
          license_type: removed.license,
          product_type: productType,
        })
        : {
          currency: "USD",
          value: Number(removed.price) || 0,
          product_type: productType,
        });
      return applyBeatPackPricing(next);
    });
  }, [applyBeatPackPricing]);

  const clear = useCallback(() => {
    void clearCart();
  }, [clearCart]);

  const replaceItems = useCallback((updater) => {
    setItems((previous) => {
      const rawNext = typeof updater === "function" ? updater(previous) : updater;
      return Array.isArray(rawNext) ? applyBeatPackPricing(rawNext) : previous;
    });
  }, [applyBeatPackPricing]);

  const count = items.length;
  const total = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.price) || 0), 0),
    [items],
  );
  const value = useMemo(() => ({
    items,
    count,
    total,
    addItem,
    addItems,
    removeItem,
    clear,
    clearCart,
    replaceItems,
    setItems,
    setDiscountPercent,
  }), [items, count, total, addItem, addItems, removeItem, clear, clearCart, replaceItems]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
